import React, { useState } from 'react'
import styled from 'styled-components'

const DrawingFAB = styled.button<{ $position: string; $active: boolean }>`
  position: absolute;
  ${({ $position }) => {
    switch ($position) {
      case 'top-left':
        return 'top: 24px; left: 16px;'
      case 'top-right':
        return 'top: 24px; right: 16px;'
      case 'bottom-left':
        return 'bottom: 24px; left: 16px;'
      case 'bottom-right':
        return 'bottom: 24px; right: 16px;'
      default:
        return 'bottom: 24px; right: 16px;'
    }
  }}
  width: 48px;
  height: 48px;
  background: var(--primary, #00ade6);
  color: white;
  border: none;
  border-radius: 24px;
  box-shadow: 0 2px 8px rgba(0, 173, 230, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  transform: ${({ $active }) => ($active ? 'rotate(45deg)' : 'rotate(0deg)')};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 173, 230, 0.4);
  }

  &:active {
    transform: ${({ $active }) =>
      $active ? 'rotate(45deg) scale(0.95)' : 'scale(0.95)'};
  }

  @media (max-width: 768px) {
    bottom: 16px;
    right: 8px;
  }
`

const DrawTools = styled.div<{ $position: string; $active: boolean }>`
  position: absolute;
  ${({ $position }) => {
    switch ($position) {
      case 'top-left':
        return 'top: 80px; left: 16px;'
      case 'top-right':
        return 'top: 80px; right: 16px;'
      case 'bottom-left':
        return 'bottom: 80px; left: 16px;'
      case 'bottom-right':
        return 'bottom: 80px; right: 16px;'
      default:
        return 'bottom: 80px; right: 16px;'
    }
  }}
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 8px;
  display: ${({ $active }) => ($active ? 'flex' : 'none')};
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
  transform-origin: ${({ $position }) => {
    switch ($position) {
      case 'top-left':
        return 'top left'
      case 'top-right':
        return 'top right'
      case 'bottom-left':
        return 'bottom left'
      case 'bottom-right':
      default:
        return 'bottom right'
    }
  }};
  animation: ${({ $active }) =>
    $active ? 'popIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'};
  z-index: 2;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`

const DrawToolButton = styled.button<{ $active?: boolean; $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${({ $danger }) =>
    $danger ? 'var(--danger, #ff3b30)' : 'var(--text-primary, #1a1a1a)'};
  font-size: 14px;
  font-weight: 400;

  &:hover {
    background: ${({ $danger, $active }) =>
      $danger
        ? 'rgba(255, 59, 48, 0.1)'
        : $active
          ? 'var(--primary-light, #e6f7fc)'
          : 'var(--bg-tertiary, #f5f5f5)'};
    color: ${({ $danger, $active }) =>
      $danger
        ? 'var(--danger, #ff3b30)'
        : $active
          ? 'var(--primary, #00ade6)'
          : 'var(--text-primary, #1a1a1a)'};
  }

  ${({ $active }) =>
    $active &&
    `
    background: var(--primary-light, #e6f7fc);
    color: var(--primary, #00ade6);
  `}
`

const ToolDivider = styled.div`
  height: 1px;
  background: var(--border-light, #f0f0f0);
  margin: 4px 8px;
`

export type DrawingTool = 'line' | 'polygon' | '3d-box' | 'measure' | null

interface MapDrawingFABProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  onToolSelect?: (tool: DrawingTool) => void
  onDeleteAll?: () => void
  activeTool?: DrawingTool
}

export const MapDrawingFAB: React.FC<MapDrawingFABProps> = ({
  position = 'bottom-right',
  onToolSelect,
  onDeleteAll,
  activeTool = null,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleFABClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  const handleToolClick = (tool: DrawingTool) => {
    onToolSelect?.(tool === activeTool ? null : tool)
    setIsOpen(false)
  }

  const handleDeleteClick = () => {
    onDeleteAll?.()
    setIsOpen(false)
  }

  const handleClickOutside = () => {
    setIsOpen(false)
  }

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen])

  return (
    <>
      <DrawingFAB
        $position={position}
        $active={isOpen}
        onClick={handleFABClick}
      >
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path
            d="M13.5 6.5L17.5 10.5M4 20h4L18.5 9.5a2.828 2.828 0 10-4-4L4 16v4z"
            stroke="currentColor"
            fill="none"
            strokeWidth="2"
          />
        </svg>
      </DrawingFAB>

      <DrawTools $position={position} $active={isOpen}>
        <DrawToolButton
          $active={activeTool === 'line'}
          onClick={() => handleToolClick('line')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M4 20L20 4"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <span>Draw Line</span>
        </DrawToolButton>

        <DrawToolButton
          $active={activeTool === 'polygon'}
          onClick={() => handleToolClick('polygon')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M12 2L2 7l10 5 10-5-10-5z"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <span>Draw Polygon</span>
        </DrawToolButton>

        <DrawToolButton
          $active={activeTool === '3d-box'}
          onClick={() => handleToolClick('3d-box')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M12 2L2 9v11l10 7 10-7V9L12 2z"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
            <path d="M12 22V12" stroke="currentColor" strokeWidth="2" />
            <path d="M12 12L2 9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 12l10-3" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>Draw 3D Box</span>
        </DrawToolButton>

        <DrawToolButton
          $active={activeTool === 'measure'}
          onClick={() => handleToolClick('measure')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M3 3L21 21"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M3 3L5 5M7 7L9 9M11 11L13 13M15 15L17 17M19 19L21 21"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
          <span>Measure Distance</span>
        </DrawToolButton>

        <ToolDivider />

        <DrawToolButton $danger onClick={handleDeleteClick}>
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
          <span>Delete All</span>
        </DrawToolButton>
      </DrawTools>
    </>
  )
}
